import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useStartAutoCuration } from '@hooks/api/discern';
import { useSnackbar } from '@hooks/useSnackbar';

import {
  buildRetryAutoCurationInput,
  type DiscernRowData,
} from '@utils/discern-transforms';

const messages = defineMessages({
  retryMissingPageGroups: {
    id: 'discernSummary.retryMissingPageGroups',
    defaultMessage:
      'Cannot retry { docName }: its page groups are missing. Re-upload the document to curate it.',
  },
});

interface UseRetryAutoCuration {
  handleRetryDocument: (rowData: DiscernRowData) => Promise<void>;
  /** Document currently being retried, so only that row's button disables. */
  retryingDocumentId: string | null;
}

/**
 * Retry a failed or stalled auto-curation job. Shared by DiscernSummary and
 * PatientDiscernTable so both tables retry the same way.
 *
 * A retry resends the document's existing page groups: the resolver detects
 * the existing job and resets it in place rather than creating a new one (see
 * post.auto_curation.resolver.ts).
 */
export function useRetryAutoCuration(): UseRetryAutoCuration {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [startAutoCuration] = useStartAutoCuration();
  const [retryingDocumentId, setRetryingDocumentId] = React.useState<
    string | null
  >(null);

  const handleRetryDocument = async (rowData: DiscernRowData) => {
    const input = buildRetryAutoCurationInput(rowData);
    // RetryAutoCurationButton already disables the button in this case, so
    // reaching here means something got past it. Say so rather than letting
    // the click do nothing.
    if (!input) {
      enqueueSnackbar({
        message: formatMessage(messages.retryMissingPageGroups, {
          docName: rowData.filename,
        }),
        variant: 'error',
      });
      return;
    }

    setRetryingDocumentId(rowData.discernDocumentId);
    try {
      await startAutoCuration(input);
    } finally {
      setRetryingDocumentId(null);
    }
  };

  return { handleRetryDocument, retryingDocumentId };
}
